import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearLogin, getRole, getUsername } from "@/lib/auth";

function TopBar({ onMenuClick }: { onMenuClick?: () => void }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const role = getRole();
  const username = getUsername();
  const isOps = role === "ops";

  // 关闭用户菜单：点击菜单外区域。
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showUserMenu) return;
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showUserMenu]);

  const handleLogout = () => {
    clearLogin();
    setShowUserMenu(false);
    navigate("/", { replace: true });
  };

  const avatarChar = (username || (isOps ? "运" : "管")).slice(0, 1).toUpperCase();

  return (
    <header className="fixed top-0 left-0 lg:left-64 right-0 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 z-40">
      {/* Left: hamburger (mobile) + breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-gray-500">
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="lg:hidden w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-menu-line text-lg"></i>
          </button>
        )}
        <span className="text-gray-400 hidden sm:inline">运营平台</span>
        <i className="ri-arrow-right-s-line text-gray-300 hidden sm:inline"></i>
        <span className="text-gray-900 font-medium truncate">
          {isOps ? "租借归还" : "今日概览"}
        </span>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-3">
        {/* Search — 仅 admin 可见，ops 不需要全站搜索 */}
        {!isOps && (
          <div className="relative">
            {showSearch ? (
              <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200">
                <i className="ri-search-line text-gray-400 ml-3"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索设备、景点、游客..."
                  className="px-2 py-2 text-sm bg-transparent outline-none w-56"
                  autoFocus
                  onBlur={() => {
                    if (!searchQuery) setShowSearch(false);
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setShowSearch(false);
                    }}
                    className="mr-2 text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowSearch(true)}
                className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
            )}
          </div>
        )}

        {/* Notifications + Language — 仅 admin 可见 */}
        {!isOps && (
          <>
            <button className="relative w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
              <i className="ri-notification-3-line text-lg"></i>
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <button className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
              <i className="ri-global-line text-lg"></i>
            </button>
          </>
        )}

        {/* User avatar + 退出菜单 */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setShowUserMenu((v) => !v)}
            className={`w-9 h-9 flex items-center justify-center rounded-lg font-semibold text-sm cursor-pointer transition-colors ${
              isOps
                ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                : "bg-teal-100 text-teal-700 hover:bg-teal-200"
            }`}
            title={username || (isOps ? "运营人员" : "管理员")}
          >
            {avatarChar}
          </button>
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {username || "—"}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {isOps ? "运营人员" : "管理员"}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2.5 flex items-center gap-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <i className="ri-logout-box-r-line text-base"></i>
                退出登录
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default TopBar;
